"use client";

import { useCallback, useState } from "react";
import { CandleDto } from "@/lib/interface/CandleDto.interface";
import { LineDto } from "@/lib/interface/LineDto.interface";
import { VectorCandleEvent } from "@/lib/interface/VectorCandleEvent.interface";
import {
  TcTrendDirection,
  TcTrendSnapshot,
  WSDeltaTrendPayload,
  WSFearGreedPayload,
  WSAuctionMarketPayload,
} from "@lacon/ws";

interface UseMarketStreamsReturn {
  candles: CandleDto[];
  plasmaLines: LineDto[];
  supLines: LineDto[];
  resLines: LineDto[];
  vectorCandles: VectorCandleEvent[];
  clearVectorCandles: () => void;
  tcTrend: TcTrendSnapshot | null;
  tcTrendChanges: { timestamp: number; trend: TcTrendDirection }[];
  clearTcTrend: () => void;
  clearTcTrendChanges: () => void;
  deltaTrend: WSDeltaTrendPayload | null;
  updateDeltaTrend: (payload: WSDeltaTrendPayload) => void;
  fearGreed: WSFearGreedPayload | null;
  updateFearGreed: (payload: WSFearGreedPayload) => void;
  clearFearGreed: () => void;
  auctionMarket: WSAuctionMarketPayload;
  updateAuctionMarket: (payload: WSAuctionMarketPayload) => void;
  clearAuctionMarket: () => void;
  updateCandles: (newCandles: CandleDto[]) => void;
  updateSingleCandle: (newCandle: CandleDto) => void;
  updateVectorCandle: (vectorCandle: VectorCandleEvent) => void;
  updatePlasmaLines: (lines: LineDto[]) => void;
  updateSupResLines: (
    supportLines: LineDto[],
    resistanceLines: LineDto[]
  ) => void;
  updateTcTrend: (snapshot: TcTrendSnapshot) => void;
}

export const useMarketStreams = (): UseMarketStreamsReturn => {
  const [candles, setCandles] = useState<CandleDto[]>([]);
  const [plasmaLines, setPlasmaLines] = useState<LineDto[]>([]);
  const [supLines, setSupLines] = useState<LineDto[]>([]);
  const [resLines, setResLines] = useState<LineDto[]>([]);
  const [vectorCandles, setVectorCandles] = useState<VectorCandleEvent[]>([]);
  const clearVectorCandles = useCallback(() => setVectorCandles([]), []);
  const [tcTrend, setTcTrend] = useState<TcTrendSnapshot | null>(null);
  const [tcTrendChanges, setTcTrendChanges] = useState<
    { timestamp: number; trend: TcTrendDirection }[]
  >([]);
  const [deltaTrend, setDeltaTrend] = useState<WSDeltaTrendPayload | null>(
    null
  );
  const [fearGreed, setFearGreed] = useState<WSFearGreedPayload | null>(null);
  const [auctionMarket, setAuctionMarket] = useState<WSAuctionMarketPayload>(
    []
  );

  const updateCandles = useCallback((newCandles: CandleDto[]) => {
    setCandles(newCandles.sort((a, b) => a.timestamp - b.timestamp));
  }, []);

  const updateSingleCandle = useCallback((newCandle: CandleDto) => {
    setCandles((prevCandles) => {
      const index = prevCandles.findIndex(
        (candle) => candle.timestamp === newCandle.timestamp
      );

      if (index !== -1) {
        const updatedCandles = [...prevCandles];
        updatedCandles[index] = newCandle;
        return updatedCandles;
      }
      const updatedCandles = [...prevCandles, newCandle];
      return updatedCandles.sort((a, b) => a.timestamp - b.timestamp);
    });
  }, []);

  const updateVectorCandle = useCallback((vectorCandle: VectorCandleEvent) => {
    setVectorCandles((prevVectorCandles) => {
      const filteredCandles = prevVectorCandles.filter(
        (vc) => vc.timestamp !== vectorCandle.timestamp
      );
      return [...filteredCandles, vectorCandle].sort(
        (a, b) => a.timestamp - b.timestamp
      );
    });
  }, []);

  const updatePlasmaLines = useCallback((lines: LineDto[]) => {
    setPlasmaLines(lines);
  }, []);

  const updateSupResLines = useCallback(
    (supportLines: LineDto[], resistanceLines: LineDto[]) => {
      setSupLines(supportLines);
      setResLines(resistanceLines);
    },
    []
  );

  const updateTcTrend = useCallback((snapshot: TcTrendSnapshot) => {
    setTcTrend((prev) => {
      const validTargets = [
        TcTrendDirection.BLUE,
        TcTrendDirection.GOLD,
        TcTrendDirection.SILVER,
      ];

      const isTarget = (d?: TcTrendDirection) =>
        !!d && validTargets.includes(d);

      const changedIntoTarget =
        isTarget(snapshot.trend) &&
        snapshot.trendPrevious !== undefined &&
        snapshot.trend !== snapshot.trendPrevious;

      const changedVsPrevState =
        !!prev && isTarget(snapshot.trend) && prev.trend !== snapshot.trend;

      if (changedIntoTarget || changedVsPrevState) {
        setTcTrendChanges((prevChanges) => {
          const last = prevChanges[prevChanges.length - 1];
          if (
            last &&
            last.timestamp === snapshot.updatedAt &&
            last.trend === snapshot.trend
          ) {
            return prevChanges;
          }
          const next = [
            ...prevChanges,
            { timestamp: snapshot.updatedAt, trend: snapshot.trend! },
          ];
          const MAX_CHANGES = 200;
          return next.length > MAX_CHANGES
            ? next.slice(next.length - MAX_CHANGES)
            : next;
        });
      }

      return snapshot;
    });
  }, []);

  const updateDeltaTrend = useCallback((payload: WSDeltaTrendPayload) => {
    setDeltaTrend(payload);
  }, []);

  const updateFearGreed = useCallback((payload: WSFearGreedPayload) => {
    setFearGreed(payload);
  }, []);

  const clearTcTrendChanges = useCallback(() => setTcTrendChanges([]), []);
  const clearTcTrend = useCallback(() => setTcTrend(null), []);
  const clearFearGreed = useCallback(() => setFearGreed(null), []);
  const updateAuctionMarket = useCallback((payload: WSAuctionMarketPayload) => {
    setAuctionMarket(payload);
  }, []);
  const clearAuctionMarket = useCallback(() => setAuctionMarket([]), []);

  return {
    candles,
    plasmaLines,
    supLines,
    resLines,
    vectorCandles,
    clearVectorCandles,
    tcTrend,
    tcTrendChanges,
    clearTcTrend,
    clearTcTrendChanges,
    deltaTrend,
    updateDeltaTrend,
    fearGreed,
    updateFearGreed,
    clearFearGreed,
    auctionMarket,
    updateAuctionMarket,
    clearAuctionMarket,
    updateCandles,
    updateSingleCandle,
    updateVectorCandle,
    updatePlasmaLines,
    updateSupResLines,
    updateTcTrend,
  };
};
